<template>
  <Modal
    :model-value="modelValue"
    :content-class="'!max-w-6xl w-full max-lg:relative'"
    :show-close="true"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <MediaForm v-if="isCreateMode" @saved="handleCreated" @cancel="handleCancelCreate" />
    <template v-else>
      <div class="flex flex-col gap-6 max-lg:pb-20">
        <div class="flex items-center justify-between">
          <h2 class="text-xl font-semibold">{{ t('pickMedia') }}</h2>
          <CVButton v-if="$can('create', 'Media')" variant="secondary" @click="isCreateMode = true">
            <UploadIcon class="size-4 text-black" /> {{ t('uploadMedia') }}
          </CVButton>
        </div>

        <div class="flex flex-col gap-4 lg:flex-row lg:gap-6">
          <div class="flex min-w-0 flex-1 flex-col gap-4">
            <div v-if="isLoading" class="flex items-center justify-center py-12">
              <Spinner class="text-hazel-600" />
            </div>

            <EntityListExceptions
              v-else
              :status="status"
              :error="error as any"
              :items-count="mediaItems.length"
              :no-items-text="t('noMediaFound')"
            />

            <MediaLibraryGrid
              v-if="mediaItems.length"
              variant="onWhiteBackground"
              :items="mediaItems"
              :selected-id="selectedMedia?.id"
              @select="handleHighlight"
            />

            <div v-if="hasNextPage" class="flex justify-center">
              <CVButton outline :is-loading="isFetchingNextPage" @click="fetchNextPage">
                {{ t('loadMore') }}
              </CVButton>
            </div>
          </div>

          <MediaPickerSidebar
            :media="selectedMedia"
            @confirm="handleConfirm"
            @cancel="handleCancel"
          />
        </div>

        <div
          class="flex justify-end gap-4 border-t border-obsidian-100 bg-white pt-4 max-lg:sticky max-lg:-bottom-6 max-lg:left-0 max-lg:right-0 max-lg:z-10 max-lg:-mx-6 max-lg:px-6 max-lg:pb-6 lg:hidden"
        >
          <CVButton variant="default" :disabled="!selectedMedia" @click="handleConfirm">
            {{ t('select') }}
          </CVButton>
          <CVButton variant="secondary" @click="handleCancel">
            {{ t('cancel') }}
          </CVButton>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import Modal from '@/components/Modal.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import MediaLibraryGrid from '@/components/media/MediaLibraryGrid.vue';
import MediaPickerSidebar from '@/components/media/MediaPickerSidebar.vue';
import MediaForm from '@/views/media/MediaForm.vue';
import { useMediaList } from '@/queries/use-media';
import { useMediaListStatusSync } from '@/queries/use-media-list-status-sync';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import type { Media as RosMedia } from '@carvia/ros-client-types';
import { Order } from '@carvia/ros-client-types';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { UploadIcon } from 'lucide-vue-next';

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'select', media: RosMedia): void;
}>();

const { t } = useI18n();

const isCreateMode = ref(false);
const selectedMedia = ref<RosMedia | null>(null);

const { data, isLoading, status, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
  useMediaList({ limit: 9, order: Order.DESC }, () => props.modelValue);

const mediaItems = useFlattenPaginatedData(data);

useMediaListStatusSync(() => props.modelValue);

const resetState = () => {
  isCreateMode.value = false;
  selectedMedia.value = null;
};

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) {
      resetState();
    }
  },
);

watch(mediaItems, (items) => {
  if (!selectedMedia.value) return;
  const updated = items.find((item) => item.id === selectedMedia.value!.id);
  if (updated) {
    selectedMedia.value = updated;
  }
});

function handleHighlight(item: RosMedia) {
  selectedMedia.value = item;
}

function handleCreated(media: RosMedia) {
  emit('select', media);
  emit('update:modelValue', false);
}

function handleCancelCreate() {
  isCreateMode.value = false;
}

function handleConfirm() {
  if (!selectedMedia.value) return;
  emit('select', selectedMedia.value);
  emit('update:modelValue', false);
}

function handleCancel() {
  emit('update:modelValue', false);
}
</script>

<i18n lang="json">
{
  "en": {
    "pickMedia": "Select media",
    "uploadMedia": "Upload video",
    "noMediaFound": "No media found.",
    "loadMore": "Load more",
    "cancel": "Cancel",
    "select": "Select"
  },
  "de": {
    "pickMedia": "Medium auswählen",
    "uploadMedia": "Video hochladen",
    "noMediaFound": "Keine Medien gefunden.",
    "loadMore": "Mehr laden",
    "cancel": "Abbrechen",
    "select": "Auswählen"
  },
  "es": {
    "pickMedia": "Seleccionar medio",
    "uploadMedia": "Subir vídeo",
    "noMediaFound": "No se encontraron medios.",
    "loadMore": "Cargar más",
    "cancel": "Cancelar",
    "select": "Seleccionar"
  }
}
</i18n>
